import { useCallback, useEffect, useMemo } from 'react';
import { ExportImageDrawer } from 'components/chart-export';
import { ToastNotification } from '@carbon/react';
import ErrorToast from 'components/error-toast/error-toast';
import { SuccessToast } from 'components/success-toast/success-toast';
import { selectedDcaScenarioAtom } from 'components/well-page-content/atoms';
import { activeTcWorkspaceAtom } from 'components/type-curve-workspace/atoms';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  InventoryTypeCurveDetailsResponse,
  InventoryWorkspaceMapArea,
  TypeCurveHeader,
} from 'types/api-responses';
import { toastAtom, tcDetailsDraftAtom, tcLateralLengthFtAtom } from './atoms';
import { PAGE_SECTIONS, PageSection } from './types';
import { DcaParametersTable } from './dca-parameters-table';
import { tcDcaFitParamsAtom } from './type-curve-dca/dca-fit-atoms';
import { SummaryTable } from './summary-table';
import { TypeCurveChartsSection } from './type-curve-charts/type-curve-charts-section';
import style from './type-curve-details-content.module.scss';
import {
  InventoryDetailsSection,
  InventorySpacingValues,
} from './inventory-details-section';
import { TypeCurveDetailsSection } from './type-curve-details-section';
import { TypeCurveDetailsForm } from './type-curve-details-form';
import {
  effectivePageSectionVisibilityAtom,
  tcFilteredUwisAtom,
  tcWellsFacetsAtom,
  tcWellsTotalCountAtom,
} from './type-curve-filters/atoms';
import { useWellFiltersInit } from './type-curve-filters/use-saved-well-filters';
import { useCrossPlotsInit } from './type-curve-filters/use-saved-cross-plots';
import { useColorByInit } from './type-curve-filters/use-saved-color-by';
import { useWellControlAreaInit } from './type-curve-filters/use-well-control-area-init';
import { useTypeCurveAreaDetails } from './use-type-curve-area-details';
import TypeCurveMap from './type-curve-map';
import { TypeCurveWellsTable } from './type-curve-wells-table';
import { UnitsTable } from './units-table';
import { InventoryManagementSection } from './inventory-management-section';
import {
  useInventoryVerifications,
  useInventoryVerificationSummary,
  useAutoVerificationStatusColorBy,
  usePromotedContextDefaults,
} from './hooks';

interface TypeCurveDetailsContentProps {
  data: TypeCurveHeader;
  // Route identifier, forwarded to TypeCurveMap (which no longer reads useParams).
  id: string | undefined;
  /** Refetch the header details API (e.g. after a verification verdict changes header-level state). */
  onRefreshHeader?: () => void;
  /** Inventory Workspace only: the selected type curve's inventory details.
   *  Absent on the plain Type Curve pages, so the section never renders there. */
  inventoryTypeCurve?: InventoryTypeCurveDetailsResponse | null;
  /** Inventory Workspace only: unpromoted workspaces edit the spacing values. */
  inventoryEditable?: boolean;
  computingInventory?: boolean;
  /** A compute error blocks Compute Inventory until the formation changes. */
  computeInventoryBlocked?: boolean;
  onComputeInventory?: (
    spacing: InventorySpacingValues
  ) => void | Promise<boolean>;
  /** Opens the Unit Builder for a unit, or a new empty draft when `null`. Only
   *  the Inventory Workspace passes it. */
  onEditSlots?: (unit: { id: number; name: string } | null) => void;
  /**
   * The Unit Builder is open over this page. Its overlay hides the content, and
   * it mounts its own map and gun barrel — both of which are singletons in
   * practice (`TypeCurveMap` publishes to the shared `tcMapRefAtom`;
   * `TypeCurveGunBarrel` drives module-level mutation atoms and a Pusher
   * subscription). So drop ours while it is open rather than run two.
   */
  unitBuilderOpen?: boolean;
  /** The page's section list. Sections absent from it never render, whatever
   *  the stored visibility says — the Inventory Workspace page has no charts. */
  sections?: readonly PageSection[];
  /** Inventory Workspace only: the workspace's areas (inline GeoJSON) + overall
   *  bbox, drawn on the map with a highlight legend. Forwarded to TypeCurveMap;
   *  absent elsewhere. */
  inventoryContext?: {
    areas: InventoryWorkspaceMapArea[];
    bbox?: [number, number, number, number];
  };
}

export const TypeCurveDetailsContent = ({
  data,
  id,
  onRefreshHeader,
  inventoryTypeCurve,
  inventoryEditable,
  computingInventory,
  computeInventoryBlocked,
  onComputeInventory,
  onEditSlots,
  unitBuilderOpen = false,
  sections = PAGE_SECTIONS,
  inventoryContext,
}: TypeCurveDetailsContentProps) => {
  const chartsOffered = sections.some((section) => section.key === 'charts');
  const [toast, setToast] = useAtom(toastAtom);
  const sectionVisibility = useAtomValue(effectivePageSectionVisibilityAtom);
  const filteredUwis = useAtomValue(tcFilteredUwisAtom);
  const selectedDcaScenario = useAtomValue(selectedDcaScenarioAtom);
  const activeTcWorkspace = useAtomValue(activeTcWorkspaceAtom);
  const dcaFitParams = useAtomValue(tcDcaFitParamsAtom);
  // Live wells count from the left sidebar (reflects currently applied filters /
  // map bounds / AOI / DCA scenario), written from the /queries/wells totalCount.
  const wellsTotalCount = useAtomValue(tcWellsTotalCountAtom);
  const [draft, setDraft] = useAtom(tcDetailsDraftAtom);
  const setLateralLengthFt = useSetAtom(tcLateralLengthFtAtom);

  useEffect(() => {
    setLateralLengthFt(data.lateralLengthFt ?? null);
  }, [data.lateralLengthFt, setLateralLengthFt]);

  const showEditableForm = activeTcWorkspace && !activeTcWorkspace.promoted;

  // Status / Development Type / Formation / Basin options come from the area
  // details endpoint (per scenario + area). Operator stays facet-driven.
  const { data: areaDetails } = useTypeCurveAreaDetails(
    data.typeCurveScenarioId,
    data.typeCurveAreaId
  );
  const statusOptions = areaDetails?.statuses ?? [];
  const developmentTypeOptions = areaDetails?.developmentTypes ?? [];
  const formationOptions = areaDetails?.formations ?? [];
  const basinOptions = areaDetails?.basins ?? [];

  const facets = useAtomValue(tcWellsFacetsAtom);
  const operatorOptions = useMemo(
    () =>
      (facets?.currentOperator?.buckets ?? [])
        .map((b) => b.name)
        .sort((a, b) => a.localeCompare(b)),
    [facets]
  );

  // Fetch inventory verification data when workspace is active
  const { refetch: refetchVerifications } = useInventoryVerifications();

  // Fetch aggregated inventory verification summary for the tasks panel
  const { refetch: refetchSummary } = useInventoryVerificationSummary();

  // Auto-inject verification status color-by rule when entering a workspace
  useAutoVerificationStatusColorBy();
  usePromotedContextDefaults();

  // Load persisted wells filters (hydrate applied filters / POST defaults).
  useWellFiltersInit();

  // Load persisted cross plots (hydrate charts) + persist subsequent changes.
  useCrossPlotsInit();

  // Load persisted color-by rules (hydrate rules) + persist subsequent changes.
  useColorByInit();

  // Preset the Global Map Filter from the persisted Well Control Area.
  useWellControlAreaInit(data);

  const onInventoryVerdictChanged = useCallback(() => {
    refetchSummary();
    onRefreshHeader?.();
  }, [refetchSummary, onRefreshHeader]);

  // Convert API-format aggregation (snake_case) to the frontend camelCase
  // used by the volume-chart dropdowns, falling back to 'p50'.
  const defaultAggregation = useMemo(() => {
    const apiAgg = data.typeWellDefaults?.aggregation;
    if (!apiAgg) return 'p50';
    const API_TO_FRONTEND: Record<string, string> = {
      arithmetic_mean: 'arithmeticMean',
      geometric_mean: 'geometricMean',
    };
    return API_TO_FRONTEND[apiAgg] ?? apiAgg;
  }, [data.typeWellDefaults?.aggregation]);

  const showSummaryTable = sectionVisibility.summaryTable;
  const hasDcaData =
    !!data.dcaForecastParameters?.length ||
    Object.keys(dcaFitParams).length > 0;
  const showDcaParams = sectionVisibility.dcaParameters && hasDcaData;
  const showTables = showSummaryTable || showDcaParams;

  return (
    <>
      {/* Flex row so the export drawer opens as a right-side aside that
          compresses the content (same push pattern as Subsurface Explorer). */}
      <div className={style.contentRow}>
        <div className={style.main}>
          {inventoryTypeCurve && sectionVisibility.inventoryDetails && (
            <InventoryDetailsSection
              data={inventoryTypeCurve}
              editable={inventoryEditable}
              computing={computingInventory}
              computeBlocked={computeInventoryBlocked}
              onCompute={onComputeInventory}
            />
          )}
          {sectionVisibility.typeCurveDetails &&
            (showEditableForm && draft ? (
              <TypeCurveDetailsForm
                workspace={activeTcWorkspace}
                draft={draft}
                onDraftChange={setDraft}
                typeCurveAreaName={data.typeCurveAreaName}
                basin={data.basin}
                // Non-promoted (draft) workspace: use the live sidebar wells
                // count, not the frozen header snapshot. `|| undefined` shows
                // `--` (not `0`) while the wells query is still loading.
                wellCount={wellsTotalCount || undefined}
                promoted={false}
                statusOptions={statusOptions}
                developmentTypeOptions={developmentTypeOptions}
                formationOptions={formationOptions}
                operatorOptions={operatorOptions}
                basinOptions={basinOptions}
              />
            ) : (
              <TypeCurveDetailsSection typeCurveData={data} />
            ))}
          {(showTables || sectionVisibility.map) && (
            <div className={showTables ? style.layout : style.layoutMapOnly}>
              {showTables && (
                <div className={style.tablesContainer}>
                  {showSummaryTable && <SummaryTable typeCurveHeader={data} />}

                  {showDcaParams && (
                    <div>
                      <DcaParametersTable
                        dcaForecastParameters={data.dcaForecastParameters ?? []}
                        dcaFitParams={dcaFitParams}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Stays mounted while the Unit Builder is open (behind the modal)
                  so closing the builder doesn't remount it — no Mapbox re-init /
                  base-tile reload on close. Its slot layer uses
                  `mapSlotGeomAtoms.main`, the builder map its own
                  `mapSlotGeomAtoms.builder`, so the two never collide and the main
                  map keeps its own geometry. */}
              {sectionVisibility.map && (
                <TypeCurveMap id={id} inventoryContext={inventoryContext} />
              )}
            </div>
          )}
          {data &&
            sectionVisibility.inventoryManagement &&
            !unitBuilderOpen && (
              <InventoryManagementSection
                data={data}
                refetchVerifications={refetchVerifications}
                onInventoryVerdictChanged={onInventoryVerdictChanged}
                inventoryEditable={inventoryEditable}
                onEditSlots={onEditSlots}
              />
            )}

          {chartsOffered && sectionVisibility.charts && selectedDcaScenario && (
            <TypeCurveChartsSection
              typeCurveHeaderDetailsId={data.id}
              dcaScenarioId={selectedDcaScenario.id}
              wells={filteredUwis}
              lateralLengthFt={data.lateralLengthFt ?? null}
              typeCurveDateCreated={data.createdAt ?? null}
              structureGridCollectionId={data.structureGridCollectionId ?? null}
              typeCurveAreaName={data.typeCurveAreaName ?? ''}
              dcaForecastParameters={data.dcaForecastParameters}
              proppantIntensityLbsFt={data.proppantIntensityLbsFt ?? null}
              fluidIntensityBblFt={data.fluidIntensityBblFt ?? null}
              fluidIntensityGalFt={data.fluidIntensityGalFt ?? null}
              horizontalWellSpacingFt={data.horizontalWellSpacingFt ?? null}
              defaultAggregation={defaultAggregation}
              typeWellDefaultDcaScenarioId={
                data.typeWellDefaults?.dcaScenarioId ?? null
              }
            />
          )}

          {data && <TypeCurveWellsTable typeCurveHeader={data} />}
          {data && sectionVisibility.unitsTable && (
            <UnitsTable typeCurveHeader={data} />
          )}
        </div>
        <ExportImageDrawer />
      </div>
      {toast && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 9999,
          }}
        >
          {toast.kind === 'error' ? (
            <ErrorToast
              message={toast.title}
              subtitle={toast.subtitle}
              onClose={() => {
                setToast(null);
                return false;
              }}
            />
          ) : toast.kind === 'info' ? (
            // Not a success: e.g. a re-landed well that left this inventory.
            <ToastNotification
              kind="info"
              title={toast.title}
              subtitle={toast.subtitle}
              lowContrast
              timeout={toast.timeout ?? 5000}
              onClose={() => {
                setToast(null);
                return false;
              }}
            />
          ) : (
            <SuccessToast
              message={toast.title}
              subtitle={toast.subtitle}
              timeout={toast.timeout}
              onClose={() => {
                setToast(null);
                return false;
              }}
            />
          )}
        </div>
      )}
    </>
  );
};
